import { ComponentItem } from "designSystem/types/component-item-types";
import sessionConstants from "csi/sessionConstants";
import { routerStore } from "csi/state/rootStore";
import { C8Node } from "csi/typings";
import { action, makeObservable, observable, override, runInAction } from "mobx";
import { createContext, useContext } from "react";
import { Team } from "src/Chat/api";
import { ChatStore, UpdateChannelOnNodeOption, updateChannelOnNode } from "src/Chat/component/ChatStore";
import { ATTR_ID_SLACK_CHANNELS, SelectedChannel } from "src/Chat/types";
import { Conversation, SLACKBOT_USER_ID, SlackApiError, User, UsersList, createChannel, getUsers, inviteUsersToChannel, setChannelPurpose } from "../api";

const INVALID_CHAR_IN_CHANNEL_NAME_REGEXP = /[^\w\p{L}_\-]/gu;

export function getLocalStorageKeyForChannel(node: C8Node): string {
	return "Slack.Channel." + node.$URL;
}

export enum WidgetPanel {
	JOIN_SLACK,
	CONNECT_TO_SLACK,
	CHANNEL_SELECTION,
	ADDING_CHANNEL,
	DISPLAYING_CHANNEL
}

export const inviteUsers = async (channelId: string, userIds: string[] = []): Promise<void> => {
	try {
		// invite our bot as well as selected users to the channel
		const invitedUsers = [sessionConstants.slackBotUserId, ...userIds];
		await inviteUsersToChannel(channelId, invitedUsers);
	} catch (error) {
		// ignore 'already_in_channel' error
		const slackApiError = (error instanceof SlackApiError) ? error.apiResponse.error : null;
		switch (slackApiError) {
			case "already_in_channel":
				// ignore
				break;
			default:
				throw error;
		}
	}
};

export class SlackStore extends ChatStore<Team, User, Conversation, SlackApiError, null, WidgetPanel> {
	@observable.ref _selectedChannel: SelectedChannel;

	//#region  properties to override

	/** the current active page in the panel */
	@override get currentPanel(): WidgetPanel {
		const { slackUserId, slackBotUserId } = sessionConstants;

		if (!slackUserId || !slackBotUserId) {
			return WidgetPanel.JOIN_SLACK;
		}

		return this._currentPanel;
	}

	/** indicate whether has any error(validation error, api error...) */
	@override get hasError(): boolean {
		const { error, channelNameErrorResource, newChannelDisplayName } = this;
		const satisfyRequired = !!newChannelDisplayName;

		return !!error || !!channelNameErrorResource || !satisfyRequired;
	}

	/** the resource for the loading placeholder */
	@override get loadingSubtitleRes(): string {
		return "LoadingSlackSubtitle";
	}

	/** ready to create channel */
	@override get readyToCreateChannel(): boolean {
		const { userDictionary } = this;
		return !!userDictionary;
	}

	/** users dictionary to specify for a new channel */
	@override get userDictionary(): ComponentItem[] | undefined {
		const { users } = this;
		return users?.map(({ id, profile }) => ({ id, name: profile?.displayName || profile?.realName }));
	}

	//#endregion

	//#region  custom properties

	/** selected channel */
	get selectedChannel(): SelectedChannel {
		return this._selectedChannel;
	}

	//#endregion

	//#region functions to override

	@override async _fetchUsers(): Promise<void> {
		try {
			const usersJson: UsersList = await getUsers();
			const currentUserId = sessionConstants.slackUserId;
			runInAction(() => {
				this._users = (usersJson.members || []).filter((user) => !user.deleted && !user.bot && user.id != currentUserId && user.id != SLACKBOT_USER_ID);
			});
		} catch (error) {
			runInAction(() => {
				this._error = error;
			});
		}
	}

	@override async _handleOnSaveNewChannel(): Promise<boolean> {
		const {
			assignSelectedChannel,
			channelIsPrivate,
			navigateToPanel,
			newChannelDescription,
			newChannelDisplayName,
			selectedUsers,
			validateChannelDescription,
			validateChannelName,
		} = this;

		if (!validateChannelDescription() || !validateChannelName) {
			return false;
		}

		const channelDescription = newChannelDescription.trim();
		const channelName = newChannelDisplayName.trim();

		try {
			const createChannelJson = await createChannel(channelName, channelIsPrivate);
			const channel = createChannelJson.channel;

			// invite our bot as well as selected users to the new channel
			await inviteUsers(channel.id, selectedUsers?.map((user) => user.id));


			if (channelDescription) {
				await setChannelPurpose(channel.id, channelDescription);
			}

			const { id, name, member, private: isPrivate } = channel;
			const selectedChannel = new SelectedChannel(id, name, member, isPrivate);
			assignSelectedChannel(selectedChannel);
			// C8-25599 cleanup wizard history
			navigateToPanel(WidgetPanel.DISPLAYING_CHANNEL, [WidgetPanel.CONNECT_TO_SLACK]);
		} catch (error) {
			// handle some known validation errors from Slack API
			const slackApiError = (error instanceof SlackApiError) ? error.apiResponse.error : null;
			let errorMessageId: string = null;
			switch (slackApiError) {
				case "name_taken":
					errorMessageId = "ChannelNameNotUnique";
					break;
				case "no_channel":
				case "invalid_name_required":
				case "invalid_name_punctuation":
				case "invalid_name_specials":
				case "invalid_name":
					errorMessageId = "ChannelNameHasWrongCharacters";
					break;
				case "invalid_name_maxlength":
					errorMessageId = "ChannelNameIsLong";
					break;
			}

			runInAction(() => {
				this._channelNameErrorResource = errorMessageId;
			});
			return false;
		}

		return true;
	};

	/** set the name of channel */
	@override _setNewChannelName(event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void {
		const { validateChannelName } = this;
		let newName = event.target.value;

		// Satisfy Slack requirements for the channel name:
		// 1. force a channel name to be lowercase
		// 2. replace all incorrect characters with dashes
		newName = newName.toLocaleLowerCase();
		newName = newName.replace(" ", "-");

		this._newChannelDisplayName = newName;
		validateChannelName();
	}

	/** initialization */
	@override initialize(): void {
		this._currentPanel = WidgetPanel.CONNECT_TO_SLACK;
	}

	/** validate the channel name */
	@override _validateChannelName(): boolean {
		const { newChannelDisplayName } = this;
		const channelName = newChannelDisplayName.trim();
		let errorResource = "";

		if (!channelName.length) {
			errorResource = "ChannelNameIsRequired";
		}

		if (channelName.length > 80) {
			errorResource = "ChannelNameIsLong";
		}

		if (channelName.match(INVALID_CHAR_IN_CHANNEL_NAME_REGEXP)) {
			errorResource = "ChannelNameHasWrongCharacters";
		}

		this._channelNameErrorResource = errorResource;

		return !errorResource;
	}

	/** validate the channel description */
	@override _validateChannelDescription(): boolean {
		let errorResource = "";

		if (this.newChannelDescription.trim().length > 250) {
			errorResource = "ChannelDescriptionIsLong";
		}

		this._channelDescriptionErrorResource = errorResource;

		return !errorResource;
	}

	//#endregion

	//#region custom functions 

	assignSelectedChannel = (channel: SelectedChannel): void => {
		const node = routerStore.locationNode;
		const { setSelectedChannel, setSelectedChannelForNode } = this;
		setSelectedChannelForNode(node, channel);
		setSelectedChannel(channel);
	};

	/** navigate to channel add panel */
	@action navigationToAddChannel = (): void => {
		const { navigateToPanel, resetAddChannelForm } = this;
		resetAddChannelForm();
		navigateToPanel(WidgetPanel.ADDING_CHANNEL);
	};

	/** reset add channel form */
	@action resetAddChannelForm = (): void => {
		this._channelNameErrorResource = null;
		this._selectedChannel = null;
		this._channelIsPrivate = false;
		this._newChannelDisplayName = "";
		this._selectedUsers = null;
	};

	/** set the selected channel */
	@action setSelectedChannel = (channel: SelectedChannel): void => {
		this._selectedChannel = channel;
	};

	setSelectedChannelForNode = (node: C8Node, channel: SelectedChannel): void => {
		localStorage.setItem(getLocalStorageKeyForChannel(node), JSON.stringify(channel));
		updateChannelOnNode(node.$URL, channel.channelId, UpdateChannelOnNodeOption.ADD, ATTR_ID_SLACK_CHANNELS);
	};

	//#endregion

	constructor() {
		super();
		makeObservable(this);
	}
}

export const SlackStoreContext = createContext<SlackStore>(undefined);
export const useSlackStore = (): SlackStore => useContext(SlackStoreContext);